import type { FocusEvent, MouseEvent as ReactMouseEvent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Lozenge from '@atlaskit/lozenge/lozenge';
import { Popup } from '@atlaskit/popup/popup';
import type { ContentProps, TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';

import { ResizeChangeListener } from '../../components/ResizeChangeListener.tsx';

import type { AttachmentActionMetadataPopupContentProps } from './AttachmentActionMetadataPopupContent.tsx';
import type { AttachmentActionExtensionContextValue } from './AttachmentActionExtensionProvider.tsx';
import { useAttachmentActionExtensionContext } from './AttachmentActionExtensionProvider.tsx';
import type { AttachmentActionMetadataDescriptor } from './parseAttachmentActionMetadata.ts';
import { AttachmentActionMetadataLoading } from './AttachmentActionMetadataLoading.tsx';

const CLOSE_GRACE_PERIOD_MS = 200;

// Keep the label bold while allowing ADS to own the full appearance palette.
const AttachmentMetadataLozenge = ({
	appearance = 'default',
	title,
}: Pick<AttachmentActionMetadataDescriptor, 'appearance' | 'title'>) => (
	<Lozenge appearance={appearance} isBold testId="forge-attachment-action-metadata-lozenge">
		<Text color="inherit" size="small" weight="bold">
			{title}
		</Text>
	</Lozenge>
);

const i18n = defineMessages({
	loadDetailsError: {
		id: 'forge-ui.attachment-action.metadata.load-details-error',
		defaultMessage: 'Unable to load attachment details',
		description: 'Error shown when Forge attachment metadata details cannot be loaded.',
	},
	retry: {
		id: 'forge-ui.attachment-action.metadata.retry',
		defaultMessage: 'Retry',
		description: 'Button label for retrying a failed Forge attachment metadata details load.',
	},
});

const styles = cssMap({
	trigger: {
		display: 'inline-flex',
		padding: token('space.0'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.050'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
	},
	popupState: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	retry: {
		alignSelf: 'start',
		padding: token('space.0'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		color: token('color.link'),
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			color: token('color.link.pressed'),
			textDecoration: 'underline',
		},
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.050'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
	},
});

type ReactLoadableLoadingProps = {
	error?: Error;
	retry?: () => void;
};

const AttachmentActionMetadataPopupLoading = ({
	descriptor,
	error,
	retry,
}: AttachmentActionMetadataPopupContentProps & ReactLoadableLoadingProps): React.JSX.Element => {
	if (error) {
		return (
			<Box role="alert" xcss={styles.popupState}>
				<Stack space="space.100">
					<Text>
						<FormattedMessage {...i18n.loadDetailsError} />
					</Text>
					{retry ? (
						<Pressable onClick={retry} xcss={styles.retry}>
							<FormattedMessage {...i18n.retry} />
						</Pressable>
					) : null}
				</Stack>
			</Box>
		);
	}

	return (
		<AttachmentActionMetadataLoading viewportSize={descriptor.extension.properties.viewportSize} />
	);
};

const AttachmentActionMetadataPopupContentLoader: LoadableType<AttachmentActionMetadataPopupContentProps> =
	LoadableLazy({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-AttachmentActionMetadataPopupContent" */ './AttachmentActionMetadataPopupContent.tsx'
				)
			).AttachmentActionMetadataPopupContent,
		isReactLoadable: true,
		loading: AttachmentActionMetadataPopupLoading,
	});

const renderPopupErrorFallback = (): React.JSX.Element => (
	<Box role="alert" xcss={styles.popupState}>
		<Text>
			<FormattedMessage {...i18n.loadDetailsError} />
		</Text>
	</Box>
);

type AttachmentActionPopupContentProps = {
	attachmentId: string;
	clearCloseTimer: () => void;
	context: AttachmentActionExtensionContextValue;
	descriptor: AttachmentActionMetadataDescriptor;
	onClose: () => void;
	onUpdate: () => void;
	popupRef: HTMLDivElement | null;
	setPopupRef: (element: HTMLDivElement | null) => void;
	scheduleClose: () => void;
};

const AttachmentActionPopupContent = ({
	attachmentId,
	clearCloseTimer,
	context,
	descriptor,
	onClose,
	onUpdate,
	popupRef,
	setPopupRef,
	scheduleClose,
}: AttachmentActionPopupContentProps): React.JSX.Element => {
	const handleBlur = useCallback(
		(event: FocusEvent<HTMLDivElement>) => {
			if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
				scheduleClose();
			}
		},
		[scheduleClose],
	);

	return (
		<div
			onBlur={handleBlur}
			onFocus={clearCloseTimer}
			onMouseEnter={clearCloseTimer}
			onMouseLeave={scheduleClose}
			ref={setPopupRef}
		>
			<ResizeChangeListener onUpdate={onUpdate} popupRef={popupRef}>
				<GenericErrorBoundary
					attribution={Attribution.PAGE_EXTENSIONS}
					renderOnError={renderPopupErrorFallback}
				>
					<AttachmentActionMetadataPopupContentLoader
						attachmentId={attachmentId}
						context={context}
						descriptor={descriptor}
						onClose={onClose}
						onInitialRender={onUpdate}
					/>
				</GenericErrorBoundary>
			</ResizeChangeListener>
		</div>
	);
};

type AttachmentActionMetadataItemProps = {
	attachmentId: string;
	context: AttachmentActionExtensionContextValue;
	descriptor: AttachmentActionMetadataDescriptor;
};

const AttachmentActionMetadataItem = ({
	attachmentId,
	context,
	descriptor,
}: AttachmentActionMetadataItemProps): React.JSX.Element => {
	const popupKey = `${attachmentId}:${descriptor.extension.id}`;
	const { closePopup, openPopup } = context;
	const [isOpen, setIsOpen] = useState(false);
	const [popupRef, setPopupRef] = useState<HTMLDivElement | null>(null);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isPinnedRef = useRef(false);
	const suppressFocusOpenRef = useRef(false);

	const clearCloseTimer = useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);
	const dismiss = useCallback(() => {
		clearCloseTimer();
		suppressFocusOpenRef.current = true;
		isPinnedRef.current = false;
		setIsOpen(false);
	}, [clearCloseTimer]);
	const close = useCallback(() => {
		dismiss();
		closePopup(popupKey);
	}, [closePopup, dismiss, popupKey]);
	const open = useCallback(() => {
		clearCloseTimer();
		suppressFocusOpenRef.current = false;
		openPopup(popupKey, dismiss);
		setIsOpen(true);
	}, [clearCloseTimer, dismiss, openPopup, popupKey]);
	const scheduleClose = useCallback(() => {
		if (isPinnedRef.current) {
			return;
		}

		clearCloseTimer();
		closeTimerRef.current = setTimeout(() => {
			// Focus events inside Custom UI do not bubble out of its iframe. Recheck
			// the parent document after focus settles before honoring a pending blur.
			if (!popupRef?.contains(popupRef.ownerDocument.activeElement)) {
				close();
			}
		}, CLOSE_GRACE_PERIOD_MS);
	}, [clearCloseTimer, close, popupRef]);

	useEffect(
		() => () => {
			clearCloseTimer();
			closePopup(popupKey);
		},
		[clearCloseTimer, closePopup, popupKey],
	);
	useEffect(() => {
		if (!isOpen) {
			isPinnedRef.current = false;
			clearCloseTimer();
		}
	}, [clearCloseTimer, isOpen]);
	useEffect(() => {
		if (
			isOpen ||
			!suppressFocusOpenRef.current ||
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Independent rollback requested for the dismissal fix.
			!fg('forge_attachment_metadata_dismiss_fix')
		) {
			return;
		}

		// Restored focus can arrive after close commits. Only fresh keyboard
		// navigation should re-enable focus-opening; hover and click call open directly.
		const allowFocusOpen = (event: KeyboardEvent) => {
			if (event.key === 'Tab') {
				suppressFocusOpenRef.current = false;
				document.removeEventListener('keydown', allowFocusOpen, true);
			}
		};
		document.addEventListener('keydown', allowFocusOpen, true);
		return () => document.removeEventListener('keydown', allowFocusOpen, true);
	}, [isOpen]);

	if (!descriptor.hasDetails) {
		return (
			<AttachmentMetadataLozenge appearance={descriptor.appearance} title={descriptor.title} />
		);
	}

	const handleClick = () => {
		if (isPinnedRef.current) {
			close();
			return;
		}

		isPinnedRef.current = true;
		open();
	};
	const handleBlur = (event: FocusEvent<HTMLButtonElement>) => {
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			scheduleClose();
		}
	};
	const handleFocus = () => {
		clearCloseTimer();
		if (!isOpen && !suppressFocusOpenRef.current) {
			open();
		}
	};
	const handlePopupClose = (event: Event | ReactMouseEvent | React.KeyboardEvent | null) => {
		// ADS also requests dismissal when the window blurs into an iframe. Our
		// Custom UI iframe is inside this popup, so that is an internal interaction.
		if (event?.type === 'blur' && popupRef?.contains(popupRef.ownerDocument.activeElement)) {
			clearCloseTimer();
			return;
		}

		close();
	};

	return (
		<Popup
			autoFocus={false}
			content={({ update }: ContentProps) => (
				<AttachmentActionPopupContent
					attachmentId={attachmentId}
					clearCloseTimer={clearCloseTimer}
					context={context}
					descriptor={descriptor}
					onClose={close}
					onUpdate={update}
					popupRef={popupRef}
					setPopupRef={setPopupRef}
					scheduleClose={scheduleClose}
				/>
			)}
			isOpen={isOpen}
			label={descriptor.title}
			onClose={handlePopupClose}
			placement="bottom-start"
			role="dialog"
			shouldFitViewport
			shouldRenderToParent
			shouldReturnFocus
			trigger={({ ref, ...triggerProps }: TriggerProps) => (
				<Pressable
					{...triggerProps}
					onBlur={handleBlur}
					onClick={handleClick}
					onFocus={
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Independent rollback requested for the dismissal fix.
						fg('forge_attachment_metadata_dismiss_fix') ? handleFocus : open
					}
					onMouseEnter={open}
					onMouseLeave={scheduleClose}
					ref={ref}
					testId="forge-attachment-action-metadata-trigger"
					xcss={styles.trigger}
				>
					<AttachmentMetadataLozenge appearance={descriptor.appearance} title={descriptor.title} />
				</Pressable>
			)}
		/>
	);
};

export type AttachmentActionMetadataListProps = {
	attachmentId: string;
	context: AttachmentActionExtensionContextValue;
	descriptors: ReadonlyArray<AttachmentActionMetadataDescriptor>;
};

export const AttachmentActionMetadataList = ({
	attachmentId,
	context,
	descriptors,
}: AttachmentActionMetadataListProps): React.JSX.Element | null => {
	if (descriptors.length === 0) {
		return null;
	}

	return (
		<Inline alignBlock="center" shouldWrap space="space.050">
			{descriptors.map((descriptor) => (
				<AttachmentActionMetadataItem
					attachmentId={attachmentId}
					context={context}
					descriptor={descriptor}
					key={descriptor.extension.id}
				/>
			))}
		</Inline>
	);
};

type AttachmentActionMetadataResolvedProps = {
	attachmentId: string;
	context: AttachmentActionExtensionContextValue;
};

const AttachmentActionMetadataResolved = ({
	attachmentId,
	context,
}: AttachmentActionMetadataResolvedProps): React.JSX.Element => (
	<AttachmentActionMetadataList
		attachmentId={attachmentId}
		context={context}
		descriptors={
			context.mode === 'attachment-specific'
				? (context.descriptorsByAttachmentId.get(attachmentId) ?? [])
				: context.extensions
		}
	/>
);

export type AttachmentActionMetadataProps = {
	attachmentId?: string | null;
};

export const AttachmentActionMetadata = ({
	attachmentId,
}: AttachmentActionMetadataProps): React.JSX.Element | null => {
	const context = useAttachmentActionExtensionContext();

	if (!context || typeof attachmentId !== 'string' || attachmentId.trim().length === 0) {
		return null;
	}

	return <AttachmentActionMetadataResolved attachmentId={attachmentId} context={context} />;
};
